<template>
    <div class="a-tabs a-tabs-v2" :style="style">
        <div class="a-tabs-rail" :class="{ light: theme !== 'dark' }">
            <div
                ref="tabRail"
                role="tablist"
                class="a-tabs-scroll"
                :class="{ 'with-prev': fadePrev, 'with-next': fadeNext, 'with-next-control': showNext }"
                :aria-label="ariaLabel"
            >
                <button
                    v-for="(tab, index) in tabsList"
                    :id="tabElementId(index)"
                    :key="tabKey(tab, index)"
                    :ref="(el) => setTabRef(el, index)"
                    class="a-tabs-item"
                    :class="{ active: index === activeIndex }"
                    :aria-controls="idPrefix && index === activeIndex ? panelElementId(index) : undefined"
                    :aria-selected="index === activeIndex"
                    :tabindex="index === focusIndex ? 0 : -1"
                    role="tab"
                    type="button"
                    @click="handleClick($event, tab, index)"
                    @keydown="handleKeydown($event, index)"
                >
                    {{ tab.label }}
                    <a-glyph
                        v-if="tab.glyph"
                        v-bind="tab.glyph"
                        :aria-hidden="true"
                        class="a-tabs-glyph"
                    />
                </button>
            </div>
            <button
                v-if="showPrev"
                class="a-tabs-control a-tabs-control-prev"
                :aria-label="prevLabel"
                type="button"
                @click="moveTo(activeIndex - 1)"
            >
                <a-glyph
                    name="i-chevron-big-left--16"
                    :fill="theme === 'dark' ? 'white' : 'brand-primary'"
                    :aria-hidden="true"
                    class="a-tabs-control-glyph"
                />
            </button>
            <button
                v-if="showNext"
                class="a-tabs-control a-tabs-control-next"
                :aria-label="nextLabel"
                type="button"
                @click="moveTo(activeIndex + 1)"
            >
                <a-glyph
                    name="i-chevron-big-right--16"
                    :fill="theme === 'dark' ? 'white' : 'brand-primary'"
                    :aria-hidden="true"
                    class="a-tabs-control-glyph"
                />
            </button>
        </div>
        <div ref="content" class="a-tabs-panel">
            <slot />
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import { useResizeObserver } from '@core/composables/useResizeObserver';
import { keyboardTargetIndex, sendTabMenuEvent, tabKey, useTabs } from '@core/composables/useTabs';

defineOptions({ name: 'ATabsV2' });

const props = defineProps({
    tabs: {
        type: Array,
        default: null,
    },
    activeTab: {
        type: Number,
        default: undefined,
    },
    controlled: {
        type: Boolean,
        default: false,
    },
    idPrefix: {
        type: String,
        default: undefined,
    },
    ariaLabel: {
        type: String,
        default: 'Tabs',
    },
    prevLabel: {
        type: String,
        default: 'Show previous',
    },
    nextLabel: {
        type: String,
        default: 'Show next',
    },
    theme: {
        type: String,
        validator: (prop) => !prop || ['light', 'dark'].includes(prop),
        default: 'light',
    },
    radius: {
        type: Number,
        default: 32,
    },
    storageKey: {
        type: String,
        default: undefined,
    },
    doNotSendGA: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['tab-click', 'tab-change']);

const source = useTabs({ props, slots: useSlots(), emit });
const { tabsList, tabsSignature, activeIndex, focusIndex, selectTab, content, tabRefs, setTabRef, tabElementId, panelElementId } = source;

const tabRail = ref(null);
const fadePrev = ref(false);
const fadeNext = ref(false);
const showPrev = ref(false);
const showNext = ref(false);

const style = computed(() => (Number.isFinite(props.radius) ? { '--a-tabs-radius': `${props.radius}px` } : {}));

function updateRailControls() {
    const rail = tabRail.value;
    fadePrev.value = false;
    fadeNext.value = false;
    showPrev.value = false;
    showNext.value = false;
    if (!rail) return;

    const scrolled = Math.abs(rail.scrollLeft);

    fadePrev.value = scrolled > 1;
    fadeNext.value = Math.ceil(scrolled + rail.clientWidth) < rail.scrollWidth - 1;
    showPrev.value = fadePrev.value && activeIndex.value > 0;
    showNext.value = fadeNext.value && activeIndex.value < tabsList.value.length - 1;
}

async function moveTo(index) {
    const next = selectTab(index);
    await nextTick();
    tabRefs.value[next]?.focus();
}

function handleClick(event, tab, index) {
    if (!props.doNotSendGA) sendTabMenuEvent(event, tab, index);
    emit('tab-click', { index, item: tab, event });
    selectTab(index);
}

function handleKeydown(event, index) {
    const nextIndex = keyboardTargetIndex(event, index, tabsList.value.length);
    if (nextIndex === null) return;
    event.preventDefault();
    moveTo(nextIndex);
}

function stateChange() {
    tabRefs.value[activeIndex.value]?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    requestAnimationFrame(updateRailControls);
}

watch(activeIndex, async () => {
    await nextTick();
    stateChange();
});

watch(tabsSignature, () => nextTick(() => requestAnimationFrame(updateRailControls)));

useResizeObserver(tabRail, updateRailControls);

onMounted(() => {
    tabRail.value?.addEventListener('scroll', updateRailControls, { passive: true });
    nextTick(() => requestAnimationFrame(updateRailControls));
});

onBeforeUnmount(() => {
    tabRail.value?.removeEventListener('scroll', updateRailControls);
});
</script>

<style lang="pcss" scoped>
.a-tabs-v2 {
    --a-tabs-radius: 16px;
    --a-tabs-control-space: 38px;
}

.a-tabs-rail.light {
    color: var(--av-nav-primary);
    background: rgba(64, 139, 234, 0.20);
    border: 1px solid var(--av-inversed-lightest);

    .a-tabs-item {
        color: var(--av-nav-primary);

        &:hover {
            background: var(--av-brand-secondary-accent);
        }

        &.active {
            background: var(--av-inversed-primary);
        }
    }

    .a-tabs-glyph,
    .a-tabs-control-glyph {
        fill: var(--av-brand-primary);
    }

    .a-tabs-item,
    .a-tabs-control {
        &:focus-visible {
            background: transparent;
        }
    }
}

.a-tabs-rail {
    padding: 8px;
    display: flex;
    overflow: hidden;
    position: relative;
    border-radius: var(--a-tabs-radius);
    background: var(--av-nav-primary);
    border: 1px solid var(--av-inversed-lightest);
}

.a-tabs-scroll {
    --fade-hide: calc(var(--a-tabs-control-space) - 8px);
    --fade-width: calc(var(--fade-hide) + 24px);
    --fade-angle: 90deg;
    gap: 0;
    width: 100%;
    display: flex;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    &.with-prev {
        mask-image: linear-gradient(var(--fade-angle), transparent var(--fade-hide), #000 var(--fade-width));
    }

    &.with-next-control {
        padding-inline-end: var(--a-tabs-control-space);
    }

    &.with-next {
        mask-image: linear-gradient(
            var(--fade-angle),
            #000 calc(100% - var(--fade-width)),
            transparent calc(100% - var(--fade-hide))
        );
    }

    &.with-prev.with-next {
        mask-image: linear-gradient(
            var(--fade-angle),
            transparent var(--fade-hide),
            #000 var(--fade-width),
            #000 calc(100% - var(--fade-width)),
            transparent calc(100% - var(--fade-hide))
        );
    }
}

.a-tabs-item {
    @mixin caption-accent;
    gap: 8px;
    border: 0;
    display: flex;
    flex: 1 1 0;
    cursor: pointer;
    padding: 0 16px;
    height: 32px;
    white-space: nowrap;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--av-inversed-primary);
    transition: background-color 300ms ease-out;
    border-radius: max(0px, calc(var(--a-tabs-radius) - 8px));

    @media (--viewport-tablet) {
        @mixin paragraph-accent;
        height: 48px;
    }

    &:hover {
        background: var(--av-brand-secondary-light);
    }

    &.active {
        background: var(--av-button-main);
    }
}

.a-tabs-item,
.a-tabs-control {
    &:focus-visible {
        outline-offset: -2px;
        background: transparent;
        outline: 2px solid var(--av-brand-secondary-light);
    }
}

.a-tabs-glyph,
.a-tabs-control-glyph {
    fill: var(--av-inversed-primary);
}

.a-tabs-control {
    inset-block-start: 50%;
    z-index: 2;
    border: 0;
    cursor: pointer;
    display: flex;
    padding: 8px;
    position: absolute;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;
    color: var(--av-inversed-primary);
    background: transparent;
}

.a-tabs-control-prev {
    inset-inline-start: 0;
    padding-inline-start: 14px;
}

.a-tabs-control-next {
    inset-inline-end: 0;
    padding-inline-end: 14px;
}

.a-tabs-panel {
    padding: 32px 0 0;
    position: relative;

    @media (--viewport-desktop-wide) {
        padding: 48px 0 0;
    }
}

.a-tabs-item {
    @media (--viewport-desktop) {
        padding: 0 24px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition-duration: 0ms;
    }
}

[dir='rtl'] {
    .a-tabs-control-glyph {
        transform: rotateY(180deg);
    }

    .a-tabs-scroll {
        --fade-angle: 270deg;
    }
}
</style>
